import { useState, useCallback, useEffect } from 'react';
import { useI18n } from '@/shared/i18n';
import { apiErrorToFieldErrors, type FieldErrors } from '@/shared/utils/validationErrorParser';

interface UseFormValidationOptions {
  /** Error from API submission to process */
  submissionError?: unknown;
  /** Callback when errors change */
  onErrorsChange?: (errors: FieldErrors) => void;
}

interface UseFormValidationReturn {
  /** Current field errors */
  errors: FieldErrors;
  /** Get error message for a specific field */
  getFieldError: (field: string) => string | undefined;
  /** Check if a field has an error */
  hasFieldError: (field: string) => boolean;
  /** Clear error for a specific field */
  clearFieldError: (field: string) => void;
  /** Clear all errors */
  clearAllErrors: () => void;
  /** Manually set errors */
  setErrors: (errors: FieldErrors) => void;
  /** Handle an API error and extract field errors */
  handleValidationError: (error: unknown) => void;
}

/**
 * Hook for managing form field validation errors with i18n support
 */
export function useFormValidation(options: UseFormValidationOptions = {}): UseFormValidationReturn {
  const { submissionError, onErrorsChange } = options;
  const { t } = useI18n();
  const [errors, setErrorsState] = useState<FieldErrors>({});

  // Process submission error when it changes
  useEffect(() => {
    if (submissionError) {
      const fieldErrors = apiErrorToFieldErrors(submissionError, t);
      setErrorsState(fieldErrors);
      onErrorsChange?.(fieldErrors);
    }
  }, [submissionError, t, onErrorsChange]);

  const getFieldError = useCallback((field: string): string | undefined => {
    return errors[field];
  }, [errors]);

  const hasFieldError = useCallback((field: string): boolean => {
    return !!errors[field];
  }, [errors]);

  const clearFieldError = useCallback((field: string): void => {
    setErrorsState((prev) => {
      if (!prev[field]) return prev;
      const { [field]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  const clearAllErrors = useCallback((): void => {
    setErrorsState({});
  }, []);

  const setErrors = useCallback((newErrors: FieldErrors): void => {
    setErrorsState(newErrors);
    onErrorsChange?.(newErrors);
  }, [onErrorsChange]);

  const handleValidationError = useCallback((error: unknown): void => {
    const fieldErrors = apiErrorToFieldErrors(error, t);
    setErrorsState(fieldErrors);
    onErrorsChange?.(fieldErrors);
  }, [t, onErrorsChange]);

  return {
    errors,
    getFieldError,
    hasFieldError,
    clearFieldError,
    clearAllErrors,
    setErrors,
    handleValidationError,
  };
}
